import { isArray, isFunction, isObject } from '@mbrtargeting/metatag-utils';
import { simpleParamsSerializer } from './url-helper.js';

// feature flag to manually enable the deprecation beacons for a short time
const ENABLED: boolean = false;
// sample rate for the beacon
const PERCENTAGE: number = 5;

const randomNumber = () => Math.round(Math.random() * 100);
const enableBeacon = ENABLED && randomNumber() < PERCENTAGE;

const beaconSent = new Set<string>(); // deduplicate beacons

/**
 * Sends a deprecation beacon to the Beacon Delivery Network (BDN).
 *
 * @param path a unique identifier for the deprecated code
 */
export const deprecationBeacon = (path: string): void => {
    try {
        if (!enableBeacon) return;
        const params = simpleParamsSerializer({
            referrer: window.location.href,
        });
        const url = `https://bdn.stroeerdigitalgroup.de/deprecation/${path}?${params}`;
        !beaconSent.has(url) && window.navigator.sendBeacon(url) && beaconSent.add(url);
    } catch (e) {
        // ignore
    }
}

/**
 * Wraps a function or object to send a deprecation beacon when accessed.
 *
 * @param path a unique identifier for the deprecated code
 * @param o the function or object to wrap
 */
export const deprecation = <T>(path: string, o: T): T => {
    if (isFunction(o)) {
        return ((...args: any[]) => {
            deprecationBeacon(path);
            return deprecation(`${path}()`, o.apply(this, args));
        }) as unknown as T;
    }
    if (isArray(o)) {
        return o.map((v, i) => deprecation(`${path}[${i}]`, v)) as T;
    }
    if (isObject(o)) {
        return new Proxy(o, {
            get: (target, prop, receiver) => {
                const propPath = `${path}.${prop.toString()}`;
                // send a beacon
                deprecationBeacon(propPath);
                // wrap the value recursively
                return deprecation(propPath, Reflect.get(target, prop, receiver));
            },
            set(target, prop, value, receiver) {
                const propPath = `${path}.${prop.toString()}`;
                // send a beacon
                deprecationBeacon(propPath);
                // set the value on the target object
                return Reflect.set(target, prop, value, receiver);
            },
        });
    }
    return o;
}
